/* ============================================================
   AM2T — Theme JS
   Vanilla ES, no dependencies.
   ============================================================ */

(() => {
  'use strict';

  document.documentElement.classList.add('js-loaded');

  /* === <drawer-element> ============================================
     Generic off-canvas drawer. Triggers are any element with
     aria-controls="<drawer-id>". Closed by [data-drawer-close],
     overlay click, Escape, or matching auto-close-media.
     Used by the mobile menu, and (Build 2) the cart drawer.
     ================================================================= */
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.overlay = this.querySelector('[data-drawer-overlay]');

      this.triggers = this.id
        ? Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`))
        : [];
      this.triggers.forEach((t) => t.addEventListener('click', this.toggle));

      this.querySelectorAll('[data-drawer-close]').forEach((el) =>
        el.addEventListener('click', this.close)
      );
      this.overlay?.addEventListener('click', this.close);

      document.addEventListener('keydown', this.onKeydown);

      const mq = this.getAttribute('auto-close-media');
      if (mq && window.matchMedia) {
        this._mql = window.matchMedia(mq);
        this._mqlHandler = (e) => {
          if (e.matches && this.isOpen) this.close();
        };
        this._mql.addEventListener?.('change', this._mqlHandler);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      this._mql?.removeEventListener?.('change', this._mqlHandler);
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    toggle = (e) => {
      e?.preventDefault();
      this.isOpen ? this.close() : this.open();
    };

    open = () => {
      if (this.isOpen) return;
      this.lastFocus = document.activeElement;
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'true'));
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(() => {
        const focusable = this.panel?.querySelector(
          '[data-drawer-close], a[href], button:not([disabled])'
        );
        focusable?.focus();
      });

      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    };

    close = () => {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      this.triggers.forEach((t) => t.setAttribute('aria-expanded', 'false'));
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';

      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }

      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    };

    onKeydown = (e) => {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key === 'Tab') this.trapFocus(e);
    };

    trapFocus(e) {
      const focusable = this.panel?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('drawer-element', DrawerElement);

  /* === <variant-picker> ============================================
     PDP variant state controller. Reads variants + options from inline
     JSON, syncs button state, price, CTA, URL, and emits 'variant:change'
     events for sibling components (gallery image sync, future cart drawer).

     Required descendants:
       <script type="application/json" data-variants>[...]</script>
       <script type="application/json" data-options>[...]</script>
       buttons with [data-color="<raw value>"] and [data-size="<raw value>"]
       a price block matching .price > .price__current
       a CTA matching [data-product-cta]
     ================================================================= */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const variantsScript = this.querySelector('[data-variants]');
      const optionsScript = this.querySelector('[data-options]');
      if (!variantsScript || !optionsScript) return;

      try {
        this.variants = JSON.parse(variantsScript.textContent);
        this.options = JSON.parse(optionsScript.textContent);
      } catch (err) {
        console.warn('[AM2T] variant-picker JSON parse failed', err);
        return;
      }

      this.colorIdx = this.options.findIndex((n) => /color|colour|colorway/i.test(n));
      this.sizeIdx = this.options.findIndex((n) => /size|talla/i.test(n));

      this.addToCartText = this.dataset.addToCartText || 'Add to cart';
      this.soldOutText = this.dataset.soldOutText || 'Sold out';
      this.unavailableText = this.dataset.unavailableText || 'Unavailable';
      this.addingText = this.dataset.addingText || 'Adding…';
      this.addedText = this.dataset.addedText || 'Added';
      this.errorText = this.dataset.errorText || 'Try again';
      this.isAdding = false;

      // Init state from active buttons (rendered by Liquid)
      this.selectedOptions = this.options.map(() => '');
      const activeColor = this.querySelector('[data-color][aria-pressed="true"]');
      if (activeColor && this.colorIdx !== -1) {
        this.selectedOptions[this.colorIdx] = activeColor.dataset.color;
      }
      const activeSize = this.querySelector('[data-size][aria-pressed="true"]');
      if (activeSize && this.sizeIdx !== -1) {
        this.selectedOptions[this.sizeIdx] = activeSize.dataset.size;
      }

      this.querySelectorAll('[data-color]').forEach((btn) => {
        btn.addEventListener('click', () =>
          this.selectOption(this.colorIdx, btn.dataset.color)
        );
      });

      this.querySelectorAll('[data-size]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.classList.contains('is-soldout')) return;
          this.selectOption(this.sizeIdx, btn.dataset.size);
        });
      });

      this.update({ skipUrl: true });

      // Wire CTA fetch (intercepts the static /cart/add link).
      // Multiple [data-product-cta] elements can coexist in the picker scope
      // (e.g., main button + <sticky-cta> mini button); event delegation
      // routes both through the same addToCart flow and keeps state in sync.
      this.ctas = Array.from(this.querySelectorAll('[data-product-cta]'));
      this.addEventListener('click', this.handleCtaClick);
    }

    selectOption(idx, value) {
      if (idx === -1 || !value) return;
      this.selectedOptions[idx] = value;
      this.update();
    }

    findVariant() {
      // Only match against options we have pickers for (color and/or size).
      // Lets us support: 4 (color+size), 3 (color only), 2 (size only),
      // and 1 (no recognized options — single SKU products like the AM2T Drop 1).
      const knownIndices = [];
      if (this.colorIdx !== -1) knownIndices.push(this.colorIdx);
      if (this.sizeIdx !== -1) knownIndices.push(this.sizeIdx);

      if (knownIndices.length === 0) {
        return this.variants.find((v) => v.available) || this.variants[0];
      }

      return this.variants.find((v) =>
        knownIndices.every((i) => v.options[i] === this.selectedOptions[i])
      );
    }

    update({ skipUrl = false } = {}) {
      const variant = this.findVariant();
      this.currentVariant = variant;
      this.updateButtons();
      this.updateColorLabel();
      this.updateSizeAvailability();
      this.updatePrice(variant);
      this.updateCta(variant);
      if (!skipUrl) this.updateUrl(variant);
      this.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant }
        })
      );
    }

    updateButtons() {
      this.querySelectorAll('[data-color]').forEach((btn) => {
        const isActive = btn.dataset.color === this.selectedOptions[this.colorIdx];
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
      this.querySelectorAll('[data-size]').forEach((btn) => {
        const isActive = btn.dataset.size === this.selectedOptions[this.sizeIdx];
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    updateColorLabel() {
      if (this.colorIdx === -1) return;
      const el = this.querySelector('.product-info__option-value');
      if (el) el.textContent = this.selectedOptions[this.colorIdx];
    }

    updateSizeAvailability() {
      if (this.colorIdx === -1 || this.sizeIdx === -1) return;
      const currentColor = this.selectedOptions[this.colorIdx];
      this.querySelectorAll('[data-size]').forEach((btn) => {
        const size = btn.dataset.size;
        const v = this.variants.find(
          (x) =>
            x.options[this.colorIdx] === currentColor &&
            x.options[this.sizeIdx] === size
        );
        const available = !!v?.available;
        btn.classList.toggle('is-soldout', !available);
        if (available) {
          btn.removeAttribute('aria-disabled');
        } else {
          btn.setAttribute('aria-disabled', 'true');
        }
      });
    }

    updatePrice(variant) {
      if (!variant) return;
      const priceEl = this.querySelector('.price');
      if (!priceEl) return;
      const current = priceEl.querySelector('.price__current');
      let compare = priceEl.querySelector('.price__compare');

      if (current) current.textContent = variant.price_formatted;

      const onSale =
        variant.compare_at_price && variant.compare_at_price > variant.price;
      priceEl.classList.toggle('price--sale', !!onSale);

      if (onSale) {
        if (!compare) {
          compare = document.createElement('span');
          compare.className = 'price__compare';
          priceEl.appendChild(compare);
        }
        compare.textContent = variant.compare_at_price_formatted;
      } else if (compare) {
        compare.remove();
      }
    }

    updateCta(variant) {
      if (!this.ctas?.length) return;

      this.ctas.forEach((cta) => {
        const labelEl = cta.querySelector('[data-cta-label]');

        if (!variant) {
          cta.setAttribute('aria-disabled', 'true');
          cta.removeAttribute('href');
          if (labelEl) labelEl.textContent = this.unavailableText;
          return;
        }

        if (variant.available) {
          cta.removeAttribute('aria-disabled');
          cta.setAttribute('href', `/cart/add?id=${variant.id}`);
          if (labelEl) labelEl.textContent = this.addToCartText;
        } else {
          cta.setAttribute('aria-disabled', 'true');
          cta.removeAttribute('href');
          if (labelEl) labelEl.textContent = this.soldOutText;
        }
      });

      // Keep the {% form 'product' %} hidden input in sync so the Shop Pay
      // / dynamic payment button submits the currently selected variant.
      if (variant) {
        this.querySelectorAll('[data-product-id-input]').forEach((input) => {
          input.value = variant.id;
        });
      }
    }

    updateUrl(variant) {
      if (!variant || !window.history?.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    handleCtaClick = (e) => {
      const cta = e.target.closest('[data-product-cta]');
      if (!cta || !this.contains(cta)) return;
      e.preventDefault();
      if (this.isAdding) return;
      if (cta.getAttribute('aria-disabled') === 'true') return;
      const variant = this.currentVariant;
      if (!variant?.available) return;
      this.addToCart(variant);
    };

    setCtaLabel(text) {
      this.ctas.forEach((cta) => {
        const labelEl = cta.querySelector('[data-cta-label]');
        if (labelEl) labelEl.textContent = text;
      });
    }

    setCtaState(stateClass) {
      this.ctas.forEach((cta) => {
        cta.classList.remove('is-loading', 'is-added', 'is-error');
        if (stateClass) cta.classList.add(stateClass);
      });
    }

    async addToCart(variant) {
      if (!this.ctas?.length) return;

      this.isAdding = true;
      this.setCtaState('is-loading');
      this.ctas.forEach((cta) => cta.setAttribute('aria-busy', 'true'));
      this.setCtaLabel(this.addingText);

      try {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: 1 }]
          })
        });
        if (!addRes.ok) throw new Error(`HTTP ${addRes.status}`);

        const cartRes = await fetch('/cart.js', {
          headers: { 'Accept': 'application/json' }
        });
        if (!cartRes.ok) throw new Error(`HTTP ${cartRes.status}`);
        const cart = await cartRes.json();

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { cart, addedItem: variant }
          })
        );

        this.setCtaState('is-added');
        this.setCtaLabel(this.addedText);

        setTimeout(() => {
          this.setCtaState(null);
          this.ctas.forEach((cta) => cta.removeAttribute('aria-busy'));
          this.isAdding = false;
          this.updateCta(this.currentVariant);
        }, 1200);
      } catch (err) {
        console.warn('[AM2T] add to cart failed', err);
        this.setCtaState('is-error');
        this.setCtaLabel(this.errorText);

        setTimeout(() => {
          this.setCtaState(null);
          this.ctas.forEach((cta) => cta.removeAttribute('aria-busy'));
          this.isAdding = false;
          this.updateCta(this.currentVariant);
        }, 2000);
      }
    }
  }

  customElements.define('variant-picker', VariantPicker);

  /* === <sticky-cta> ================================================
     Mobile-only sticky bar with mirror Add-to-cart button. UVU/Bandit-style:
     visibility is handled BY CSS (default visible on mobile, display:none
     at ≥1024px). JS is responsible only for:
       - keeping aria-hidden in sync with the active breakpoint
       - updating the displayed price on variant:change events
     This decoupling means the bar is reachable even if JS hydration is
     delayed or fails (iOS Safari custom-element edge cases observed).
     ================================================================= */
  class StickyCta extends HTMLElement {
    connectedCallback() {
      this.priceEl = this.querySelector('[data-sticky-price]');

      this.mediaQuery = window.matchMedia('(max-width: 1023px)');
      this.updateAria(this.mediaQuery.matches);
      this.mediaQuery.addEventListener('change', this.onMediaChange);

      this.addEventListener('variant:change', this.onVariantChange);
    }

    disconnectedCallback() {
      this.mediaQuery?.removeEventListener('change', this.onMediaChange);
      this.removeEventListener('variant:change', this.onVariantChange);
    }

    updateAria = (onMobile) => {
      this.setAttribute('aria-hidden', onMobile ? 'false' : 'true');
    };

    onMediaChange = (e) => {
      this.updateAria(e.matches);
    };

    onVariantChange = (e) => {
      const v = e.detail?.variant;
      if (!v || !this.priceEl) return;
      this.priceEl.textContent = v.price_formatted;
    };
  }

  customElements.define('sticky-cta', StickyCta);

  /* === Cart counter sync ============================================
     Listens for 'cart:update' (dispatched after add-to-cart fetches) and
     updates the header cart counter with a brief scale microanimation.
     The cart drawer (Build 2D) will hook into the same event.
     ================================================================= */
  document.addEventListener('cart:update', (e) => {
    const cart = e.detail?.cart;
    if (!cart) return;

    const cartLink = document.querySelector('[data-cart-link]');
    if (!cartLink) return;

    let counter = cartLink.querySelector('.site-header__cart-count');

    if (cart.item_count > 0) {
      if (!counter) {
        counter = document.createElement('span');
        counter.className = 'site-header__cart-count';
        counter.setAttribute('aria-hidden', 'true');
        cartLink.appendChild(counter);
      }
      counter.textContent = cart.item_count;

      const baseLabel = cartLink.dataset.cartLabelBase;
      if (baseLabel) {
        cartLink.setAttribute('aria-label', `${baseLabel} (${cart.item_count})`);
      }

      counter.animate?.(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.1)' },
          { transform: 'scale(1)' }
        ],
        { duration: 200, easing: 'ease-out' }
      );
    } else if (counter) {
      counter.remove();
    }
  });
})();
